import { Action, createReducer, on } from '@ngrx/store';
import { AppStatus } from '@wdx/clmi/clmi-swagger-gen';
import { CrudState, CrudStateObject } from '@wdx/clmi/api-services/models';
import * as appsActions from './apps.actions';
import * as R from 'ramda';

export interface AppContext<T = any> {
    context: T;
    id: string;
}

export interface State {
    apps: CrudStateObject<AppStatus>;
    appContext: Record<string, AppContext<any>>;
}

export const initialState: State = {
    apps: {},
    appContext: {},
};

const reducerSetup = createReducer(
    initialState,

    on(appsActions.upsertAppContext, (state, props): State => {
        if (!props.context?.id) {
            return state;
        }

        const currentContext =
            state.appContext?.[props.context?.id]?.context ?? {};
        const updatedContext = { ...currentContext, ...props?.context };
        return {
            ...state,
            appContext: {
                ...state.appContext,
                [props.context.id]: updatedContext,
            },
        };
    }),

    on(appsActions.clearAppContext, (state, props): State => {
        if (state.appContext?.[props.id]) {
            const appContext = R.clone(state.appContext) || {};
            delete appContext[props.id];
            return { ...state, appContext };
        }
        return state;
    }),

    on(
        appsActions.getAppStatus,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...(state.apps[props.appId] ||
                        ({} as CrudState<AppStatus>)),
                    isLoadingSingle: true,
                    hasLoadingSingleError: false,
                },
            },
        }),
    ),

    on(
        appsActions.getAppStatusSuccess,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...state.apps[props.appId],
                    isLoadingSingle: false,
                    hasLoadingSingleError: false,
                    single: props.app,
                },
            },
        }),
    ),

    on(
        appsActions.getAppStatusFailure,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...state.apps[props.appId],
                    isLoadingSingle: false,
                    hasLoadingSingleError: true,
                },
            },
        }),
    ),

    on(
        appsActions.confirm,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...(state.apps[props.appId] ||
                        ({} as CrudState<AppStatus>)),
                    isUpdating: true,
                    hasUpdatingError: false,
                },
            },
        }),
    ),

    on(
        appsActions.confirmSuccess,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...state.apps[props.appId],
                    isUpdating: false,
                    hasUpdatingError: false,
                },
            },
        }),
    ),

    on(
        appsActions.confirmFailure,
        (state, props): State => ({
            ...state,
            apps: {
                ...state.apps,
                [props.appId]: {
                    ...state.apps[props.appId],
                    isUpdating: false,
                    hasUpdatingError: true,
                },
            },
        }),
    ),
);

export function reducer(state: State | undefined, action: Action) {
    return reducerSetup(state, action);
}
